import { ChangeDetectorRef, Component, EventEmitter, input, Input, OnInit, Output } from '@angular/core';
import { LocalStorageKeys } from '@app/services/local-storage.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FieldLength } from '@app/origami-api/constants/fields-length';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { NgModel } from '@angular/forms';
import { DialogWindowResponse } from '@app/origami-api/constants/dialog-window-response';
import { ButtonThemeColor } from '@progress/kendo-angular-buttons';
import { SIGNAL } from '@angular/core/primitives/signals';

export interface PromtDialogOptions {
  title: string;
  titleSuffix?: string;
  inputType?: 'textarea' | 'input';
  label?: string;
  required?: boolean;
  btnOkTitle?: string;
  btnCancelTitle?: string;
  defaultValue?: string;
  text?: string;
  maxLength: number | string;
  pattern: RegExp;
  yesHandler?: (text: string) => Promise<{ close: boolean }>;
  cancelHandler?: () => Promise<{ close: boolean }>;
  textClass?: string;
  errorMessage: string;
  errorMaxLengthMessage: string;
  modernBehavior?: boolean;
}

export interface PromptDialogComponentResult {
  action: DialogWindowResponse;
  text: string;
  additionalValue?: any;
}

@Component({
  standalone: false,
  selector: 'ti-dialog',
  templateUrl: 'prompt.component.html',
  styleUrls: ['prompt.component.sass'],
})
export class PromptDialogComponent implements OnInit {
  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {}

  readonly strings = tiStrings.app.misc;
  readonly localStorageKeys = LocalStorageKeys;
  readonly DialogWindowResponse = DialogWindowResponse;

  @Input() set options(opt: PromtDialogOptions) {
    this.title = opt.title;
    this.titleSuffix = opt.titleSuffix || '';
    if (opt.defaultValue) {
      this.value = opt.defaultValue;
    }

    this.maxLength = opt.maxLength;
    this.pattern = opt.pattern;
    this.errorMessage = opt.errorMessage;
    this.errorMaxLengthMessage = opt.errorMaxLengthMessage;

    this.inputType = opt.inputType || 'textarea';
    this.label = opt.label;
    this.text = opt.text && this.sanitizer.bypassSecurityTrustHtml(opt.text);
    this.required = opt.required;
    this.btnOkTitle = opt.btnOkTitle;
    this.btnCancelTitle = opt.btnCancelTitle;
    if (opt.textClass) {
      this.textClass = opt.textClass;
    }
    this.yesHandler = opt.yesHandler;
    this.cancelHandler = opt.cancelHandler;
    this.modernBehavior[SIGNAL].value = opt?.modernBehavior;
  }

  @Input() errorMessage = tiStringsFormat(
    this.strings.fieldMaximumLengthPattern,
    FieldLength.MaxFieldLength.topicAttributeMaxStringLength,
  );
  @Input() errorMaxLengthMessage = tiStringsFormat(
    this.strings.fieldMaximumLengthPattern,
    FieldLength.MaxFieldLength.topicAttributeMaxStringLength,
  );
  public modernBehavior = input<boolean>(false);
  @Input() isLoading: boolean = false;
  @Input() title: string;
  @Input() titleSuffix: string = '';
  @Input() inputType: 'textarea' | 'input' = 'textarea';
  @Input() label: string;
  @Input() text: SafeHtml;
  @Input() textClass: string = ''; // TODO: remove and add primary/secondary texts instead
  @Input() required: boolean;
  @Input() btnOkTitle: string;
  @Input() btnOkClass: string = '';
  @Input() btnCancelTitle: string;
  @Input() titleClass: string = '';
  @Input() yesHandler: (text: string) => Promise<{ close: boolean }>;
  @Input() cancelHandler: () => Promise<{ close: boolean }>;
  @Input() maxLength: number | string = FieldLength.MaxFieldLength.topicAttributeMaxStringLength;
  @Input() pattern: RegExp;
  @Input() maxWidth: number = 0;
  @Input() themeColor: ButtonThemeColor = null;
  @Output() close = new EventEmitter<PromptDialogComponentResult>();

  value: string = '';
  isFieldInvalid: boolean;

  ngOnInit(): void {
    this.maxWidth = this.maxWidth || Math.round(window.innerWidth / 2);
    this.isFieldInvalid = this.required === undefined ? false : this.required && !this.value;
  }

  protected titleColorByThemeColor() {
    const prefix = 'promt-dialog-color';
    if (!this.themeColor) return `${prefix}--default`;
    switch (this.themeColor) {
      case 'error':
        return `${prefix}--error`;
      case 'warning':
        return `${prefix}--warning`;
      case 'success':
        return `${prefix}--success`;
      default:
        return `${prefix}--default`;
    }
  }

  buttonClick(result: DialogWindowResponse): void {
    if (this.isDisabled && this.modernBehavior()) return;
    if (result === DialogWindowResponse.yes && this.yesHandler) {
      this.doAction(result, () => this.yesHandler(this.value));
    } else if (result === DialogWindowResponse.no && this.cancelHandler) {
      this.doAction(result, this.cancelHandler);
    } else {
      this.emitClose(result);
    }
  }

  get isDisabled() {
    return (this.required && !this.value.trim()) || this.isLoading || this.isFieldInvalid;
  }

  private doAction(result: DialogWindowResponse, handler: () => Promise<{ close: boolean }>) {
    this.isLoading = true;

    handler()
      .then((handlerRes) => {
        this.isLoading = false;

        if (handlerRes.close) {
          this.emitClose(result);
        }
      })
      .catch(() => (this.isLoading = false));
  }

  private emitClose(result: DialogWindowResponse): void {
    this.close.emit({
      action: result,
      text: this.value,
    });
  }

  public validateField(field: NgModel) {
    // todo: may be split that part of the logic in additional directive(?) for such common cases
    this.isFieldInvalid = field.invalid;
    if (field.value && field.value.length > this.maxLength && field.valid) {
      this.setFieldErrors(field, { maxlength: true });
      return;
    }

    if (field.value && field.value.length < this.maxLength && field.invalid) {
      this.setFieldErrors(field, { maxlength: false });
    }

    if (field.value && this.pattern) {
      this.setFieldErrors(field, this.pattern.test(field.value) ? null : { errorText: true });
    }
  }

  private setFieldErrors(field: NgModel, errors: any) {
    field.control.setErrors(errors);
    this.isFieldInvalid = field.invalid;
    this.cdr.detectChanges();
  }
}
